import { Button } from '@crowdfpl/ui-web/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@crowdfpl/ui-web/dialog';
import { Input } from '@crowdfpl/ui-web/input';
import { Label } from '@crowdfpl/ui-web/label';
import { Separator } from '@crowdfpl/ui-web/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@crowdfpl/ui-web/tabs';
import { Loader2 } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { API_CONFIG } from '@/api/client';
import { GoogleIcon } from '@/components/shared/GoogleIcon';
import { XIcon } from '@/components/shared/XIcon';
import { AUTH_BASE_PATH } from '@/constants';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (emailOrUsername: string, password: string, token?: string) => Promise<void> | void;
  onRegister: (email: string, username: string, password: string, token?: string) => Promise<void> | void;
  onForgot?: (email: string) => Promise<void> | void;
  contextMessage?: string;
  isLoginLoading?: boolean;
  isRegisterLoading?: boolean;
  isForgotLoading?: boolean;
}

export function AuthModal({ isOpen, onClose, onLogin, onRegister, onForgot, contextMessage, isLoginLoading = false, isRegisterLoading = false, isForgotLoading = false }: AuthModalProps) {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLoading = isLoginLoading || isRegisterLoading || isForgotLoading || isSubmitting;

  const onLoginSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData(e.currentTarget);
      const usernameOrEmail = String(formData.get('usernameOrEmail') || '');
      const password = String(formData.get('password') || '');
      await onLogin(usernameOrEmail, password);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRegisterSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData(e.currentTarget);
      const email = String(formData.get('email') || '');
      const username = String(formData.get('username') || '');
      const password = String(formData.get('password') || '');
      await onRegister(email, username, password);
      onClose();
    } catch (err) {
      // Keep modal open on error; parent should surface feedback
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTwitterSignIn = () => {
    window.location.href = `${API_CONFIG.BASE_URL}${AUTH_BASE_PATH}/twitter?mode=sign_in_up`;
  };

  const handleGoogleSignIn = () => {
    window.location.href = `${API_CONFIG.BASE_URL}${AUTH_BASE_PATH}/google?mode=sign_in_up`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>{contextMessage || 'Welcome to CrowdFPL'}</DialogTitle>
          <DialogDescription className="sr-only">
            Sign in or create a CrowdFPL account to vote, track your predictions and compete with other managers.
          </DialogDescription>
        </DialogHeader>
        <Tabs value={activeTab} onValueChange={(v) => { if (!isLoading) { setActiveTab(v as 'login' | 'register'); setShowForgot(false); } }}>
          <TabsList className="grid grid-cols-2 h-auto w-full mb-4">
            <TabsTrigger value="login" disabled={isLoading}>Login</TabsTrigger>
            <TabsTrigger value="register" disabled={isLoading}>Register</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleTwitterSignIn}
                  disabled={isLoading}
                >
                  <XIcon className="mr-2 h-4 w-4" />
                  Sign in with X
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                >
                  <GoogleIcon className="h-4 w-4 mr-2" />
                  Sign in with Google
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <form onSubmit={onLoginSubmit} className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="usernameOrEmail">Email or Username</Label>
                  <Input id="usernameOrEmail" name="usernameOrEmail" autoComplete="username" placeholder="you@example.com or username" required disabled={isLoading} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" name="password" type="password" autoComplete="current-password" required disabled={isLoading} />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoginLoading || isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>
            </div>
            <div className="mt-3 text-right">
              <Button type="button" variant="link" className="px-0 text-sm" onClick={() => setShowForgot((v) => !v)}>
                Forgot password?
              </Button>
            </div>
            {showForgot && (
              <form
                className="mt-2 space-y-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (onForgot && forgotEmail) {
                    await onForgot(forgotEmail);
                    setShowForgot(false);
                    setForgotEmail('');
                  }
                }}
              >
                <div className="space-y-1">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" onClick={() => setShowForgot(false)} disabled={isLoading}>Cancel</Button>
                  <Button type="submit" disabled={isLoading}>
                    {isForgotLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </Button>
                </div>
              </form>
            )}
          </TabsContent>

          <TabsContent value="register">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleTwitterSignIn}
                  disabled={isLoading}
                >
                  <XIcon className="mr-2 h-4 w-4" />
                  Sign up with X
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                >
                  <GoogleIcon className="h-4 w-4 mr-2" />
                  Sign up with Google
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <form onSubmit={onRegisterSubmit} className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" autoComplete="email" placeholder="you@example.com" required disabled={isLoading} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="username">Username</Label>
                  <Input id="username" name="username" autoComplete="username" placeholder="your-username" required disabled={isLoading} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="reg_password">Password</Label>
                  <Input id="reg_password" name="password" type="password" autoComplete="new-password" required disabled={isLoading} />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isRegisterLoading || isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create account'
                  )}
                </Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

